/**
 * Quick add (AB#525).
 *
 * Three behaviours, chosen per product by snippets/mlr-quick-add-mode and written onto the card
 * as `data-mode`. This file does not decide; it does what the card says.
 *
 *   instant  one purchasable variant and nothing to choose. Adds straight to the cart.
 *   sheet    opens the quick-add sheet, which fetches the product's own options.
 *   link     no quick add. Events, downloads and anything with no purchasable variant.
 *
 * The `instant` path will only add a variant the SERVER named, via `data-variant-id`, which is
 * written only when the mode is `instant`. A sheet product has no id on its card, so no amount
 * of tampering with `data-mode` can add an unchosen variant to a cart.
 */

import { resolveVariant, readVariants } from './mlr-variant-form.js';

const SHEET_ID = 'mlr-quick-add-sheet';

/** Shopify's cart API wants form-encoded or JSON; JSON keeps the shapes obvious. */
async function addToCart(id, quantity = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
  });
  if (!res.ok) {
    let message = `Cart responded ${res.status}`;
    try {
      const body = await res.json();
      if (body?.description) message = body.description;
    } catch {
      /* keep the status-code message */
    }
    throw new Error(message);
  }
  return res.json();
}

/** Tell the rest of the theme a line was added, so the cart bubble and drawer can react. */
function announceCartChange(detail) {
  document.dispatchEvent(new CustomEvent('mlr:cart-added', { bubbles: true, detail }));
  // Horizon listens for its own event name.
  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

function setBusy(button, busy) {
  button.toggleAttribute('data-busy', busy);
  button.setAttribute('aria-busy', busy ? 'true' : 'false');
}

/* ------------------------------------------------------------------ sheet */

function sheetEl() {
  return document.getElementById(SHEET_ID);
}

/**
 * The sheet is OPENED declaratively, by `data-mlr-sheet-open` on the card button, which
 * mlr-sheet.js already handles. This file does not open it: doing that here as well would mean
 * two code paths racing to show the same dialog, and focus handling would belong to neither.
 */
function closeSheet() {
  sheetEl()?.querySelector('[data-mlr-sheet-close]')?.click();
}

async function loadIntoSheet(productUrl) {
  const slot = document.querySelector('[data-mlr-quick-add-slot]');
  if (!slot) return;

  slot.innerHTML = '<p class="mlr-quick-add__loading">Loading options...</p>';

  try {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('view', 'quick-add');
    const res = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`Options responded ${res.status}`);
    slot.innerHTML = await res.text();
    wireForm(slot);
  } catch (err) {
    // A failed fetch must not strand the shopper in a sheet with nothing in it.
    slot.innerHTML = `<p class="mlr-quick-add__error">Could not load options. <a href="${productUrl}">Open the product page</a>.</p>`;
  }
}

/* ------------------------------------------------------------------ form */

/**
 * Resolve the current selection to a variant and keep the hidden id in step.
 *
 * The option VALUES are matched in the product's own option order, which is the order
 * mlr-option-groups renders and the order Shopify stores `variant.options` in. Matching by
 * position rather than by name is what makes this work for the JJC products, whose first option
 * is called "Size" but is not one.
 */
function wireForm(root) {
  const host = root.querySelector('[data-mlr-option-select]');
  const form = root.querySelector('[data-mlr-quick-add-form]');
  if (!form) return;

  const idInput = form.querySelector('[data-mlr-variant-id]');
  const addButton = form.querySelector('[data-mlr-add]');
  const hint = form.querySelector('[data-mlr-add-hint]');

  // Shared with the product page, deliberately. The matching rule is by POSITION in the
  // product's own option order, and a second copy of it is a second chance to get the JJC
  // "Extras" products wrong, whose first option is called "Size" and is not one.
  const variants = readVariants(form);
  const resolve = (selections) => resolveVariant(variants, selections);

  host?.addEventListener('mlr-option:change', (event) => {
    const { complete, selections } = event.detail ?? {};
    const variant = complete ? resolve(selections) : null;
    if (idInput) idInput.value = variant ? String(variant.id) : '';

    // The component owns enabling the button on completeness. This adds the one thing it cannot
    // know: a complete selection can still be a variant that does not exist or is sold out.
    if (addButton && complete) {
      const unavailable = !variant || variant.available === false;
      addButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      if (hint) hint.textContent = unavailable ? 'That combination is not available.' : '';
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (addButton?.getAttribute('aria-disabled') === 'true') return;

    const id = idInput?.value;
    if (!id) return;

    setBusy(addButton, true);
    try {
      const result = await addToCart(id, 1);
      announceCartChange(result);
      closeSheet();
    } catch (err) {
      if (hint) hint.textContent = err.message;
    } finally {
      setBusy(addButton, false);
    }
  });
}

/* ------------------------------------------------------------------ cards */

/*
 * THE CONTROL ALREADY WORKS BEFORE THIS FILE RUNS. The card renders a real <form> for a
 * one-size product and a real <a> for everything else, so a tap in the gap before this module
 * is attached submits the form or follows the link instead of being discarded. What follows
 * UPGRADES those controls; it is not what makes them work.
 *
 * `submit` rather than `click` for the instant mode, because the form is the thing that acts:
 * listening for submit catches the Enter key and any other way a browser submits, and leaves
 * the no-JS path untouched if this file never arrives.
 */
document.addEventListener('submit', async (event) => {
  /*
   * `data-mlr-card-add-form`, NOT `data-mlr-quick-add-form`.
   *
   * The sheet owned that second name first: product.quick-add.liquid puts it on the form
   * INSIDE the quick-add sheet, and line 98 above reads it there. Reusing it for the card's
   * cart form made three card forms answer to a selector that expected exactly one, which
   * broke a sheet test on strict mode, and quietly pointed this submit handler at the sheet's
   * form as well.
   *
   * Same mistake as `data-mlr-quick-add` versus `data-mlr-quick-add-ready` a few hours
   * earlier: a new attribute dropped into an existing namespace without asking who else
   * answers to it. Check first; the grep is free.
   */
  const form = event.target.closest('[data-mlr-card-add-form]');
  if (!form) return;
  const button = form.querySelector('[data-mlr-quick-add]');
  if (!button) return;

  const productUrl = button.getAttribute('data-product-url');
  const variantId = button.getAttribute('data-variant-id');

  // Nothing to improve on: let the browser submit to /cart/add as it would without this file.
  if (!variantId) return;

  event.preventDefault();
  setBusy(button, true);
  try {
    const result = await addToCart(variantId, 1);
    announceCartChange(result);
  } catch {
    // The fetch failed, so fall back to the submit this handler just prevented. The shopper
    // still gets the item, via /cart, which is what the form would have done unaided.
    form.submit();
  } finally {
    setBusy(button, false);
  }
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-mlr-quick-add]');
  if (!button) return;

  const mode = button.getAttribute('data-mode');
  const productUrl = button.getAttribute('data-product-url');

  // `link` is already an anchor to the product page. Let the browser do it.
  if (mode === 'link') return;

  // `instant` is a submit button inside the form handled above. Let the submit happen.
  if (mode === 'instant') {
    return;
  }

  if (mode === 'sheet') {
    // mlr-sheet.js has already opened the sheet from this same click, and preventDefaulted the
    // anchor. If the sheet is not in the DOM, do NOT preventDefault: the anchor's own href is
    // the product page, which is the right destination and the one a shopper without this file
    // would have reached.
    if (!sheetEl()) return;
    if (productUrl) await loadIntoSheet(productUrl);
  }
});

/*
 * The listener above is DELEGATED on `document` and this file is a module, so it is deferred:
 * between the card painting and this line running, a quick-add button is in the DOM, looks
 * pressable, and does nothing at all when pressed. The event has no listener to reach and is
 * simply lost.
 *
 * This marker is how anything can tell the difference. It cost a flaky gate failure to find:
 * the cart suite clicked quick add on a cold page, the click landed before this file ran, and
 * the test waited fifteen seconds for a cart line that was never going to appear, then passed
 * on the retry because the second load was warm. The symptom pointed at the cart; the cause
 * was here.
 *
 * It is one attribute and it is also the honest readiness signal for a real shopper: anything
 * that wants to know whether quick add is live should ask, rather than assume that markup
 * implies behaviour.
 *
 * THE NAME MATTERS. `dataset.mlrQuickAdd` would render as `data-mlr-quick-add`, which is the
 * attribute the CARDS carry, so `<html>` would join every `[data-mlr-quick-add]` query on the
 * page: the collection grid's own count went from 24 cards to 25 the moment this was added,
 * and the suite caught it. `-ready` keeps the two apart, because an attribute selector matches
 * the whole name and never a prefix.
 */
document.documentElement.dataset.mlrQuickAddReady = 'true';
